import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImportIcon, SearchIcon } from "../../../assets/composer-editor-icons";
import { CircularProgress, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../tools/axiosConfig";
import { decodeJSONFromServer } from "../../../tools/admin_enums";
import useErrorPopup from "../../../tools/hooks/showError";
import useLongAction from "../../shared/long-action/useLongAction";
import {
  buildActionFallback,
  formatApiError,
  formatLoadError,
  formatUploadError,
  validateFile,
} from "../../../utils/fileUploadHelpers";

const QUESTION_IMPORT_FILE_RULES = { maxSizeMB: 10, acceptedTypes: [".json", "application/json"] };
const formatQuestionImportError = (error) =>
  formatUploadError(error, buildActionFallback("import", "classification question"));

export const LLM_QUESTIONS_REFRESH_EVENT = "composer-llm-questions-refresh";
export const LLM_QUESTIONS_UPDATED_EVENT = "composer-llm-questions-updated";

/**
 * Notify the composer sidebar that the questions list may need a refresh.
 * Fired whenever this page mutates the list (create/delete/save) so the
 * LLM questions tree stays in sync.
 */
const notifyUpdated = () => {
  try {
    window.dispatchEvent(new CustomEvent(LLM_QUESTIONS_UPDATED_EVENT));
  } catch (_) {
    /* no-op */
  }
};

const getChosenPromptLlmLabel = (question) => {
  if (!question?.active_prompt_id || !Array.isArray(question?.prompts)) {
    return "-";
  }
  const activePrompt = question.prompts.find((p) => p?.id === question.active_prompt_id);
  return activePrompt?.llm_model_key || "-";
};

const formatStat = (value, { digits = 1 } = {}) => {
  if (value == null) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${n.toFixed(digits)}%`;
};

/**
 * Non-expandable LLM questions overview table. Clicking a row navigates to the
 * per-question detail page (`/composer/courses/:courseId/llm-questions/:questionId`).
 */
const LlmQuestionsList = ({ courseId }) => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [search, setSearch] = useState("");
  const [runningAllTests, setRunningAllTests] = useState(false);
  const [runAllProgress, setRunAllProgress] = useState({ completed: 0, total: 0 });
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);
  const currentCourseIdRef = useRef(courseId);
  currentCourseIdRef.current = courseId;
  const listRequestRef = useRef(0);
  const longAction = useLongAction();
  const [showError, ErrorPopupComponent] = useErrorPopup();

  // Only the latest list request writes the list, so a slow answer for the course the author
  // left, or an older answer for this one, cannot replace it or raise its error.
  const fetchQuestions = useCallback(async () => {
    const request = ++listRequestRef.current;
    if (!courseId) {
      setQuestions([]);
      return;
    }
    try {
      const { data } = await apiClient.get(
        `/backoffice/v1/courses/${encodeURIComponent(courseId)}/classification-questions/`
      );
      if (listRequestRef.current !== request) return;
      setQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      if (listRequestRef.current !== request) return;
      console.error(err);
      showError(formatLoadError(err, "classification questions"));
      setQuestions([]);
    }
  }, [courseId, showError]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    const handler = () => {
      fetchQuestions();
    };
    window.addEventListener(LLM_QUESTIONS_REFRESH_EVENT, handler);
    return () => window.removeEventListener(LLM_QUESTIONS_REFRESH_EVENT, handler);
  }, [fetchQuestions]);

  const displayedQuestions = useMemo(() => {
    const list = questions || [];
    if (!search?.trim()) return list;
    const needle = search.trim().toLowerCase();
    return list.filter((q) => (q.name || "").toLowerCase().includes(needle));
  }, [questions, search]);

  const runnableQuestions = useMemo(
    () =>
      (questions || []).filter(
        (item) =>
          item?.id && item?.definition && item?.tests?.length && item?.prompts?.length
      ),
    [questions]
  );

  const runAllDisabledReason = useMemo(() => {
    if (runningAllTests) return "already running all CQ tests";
    if (!courseId) return "no program context";
    if (!questions?.length) return "no classification questions available";
    if (!runnableQuestions.length) return "no questions have both tests and prompts";
    return "";
  }, [courseId, questions, runnableQuestions.length, runningAllTests]);

  const runAllQuestionTests = useCallback(async () => {
    if (runAllDisabledReason) {
      if (runAllDisabledReason !== "already running all CQ tests") {
        showError(`Can't run all CQ tests: ${runAllDisabledReason}`);
      }
      return;
    }

    const total = runnableQuestions.length;
    const skippedCount = (questions || []).length - total;
    const failures = [];
    const failureMessages = [];

    setRunningAllTests(true);
    setRunAllProgress({ completed: 0, total });

    try {
      for (let index = 0; index < runnableQuestions.length; index += 1) {
        const question = runnableQuestions[index];
        setRunAllProgress({ completed: index, total });
        try {
          const { data: builderResponse } = await apiClient.post(
            `/backoffice/v1/courses/${encodeURIComponent(courseId)}/classification-builder/init/`,
            { classification_question_id: question.id }
          );
          const builder = decodeJSONFromServer(builderResponse);
          await apiClient.post(
            `/backoffice/v1/courses/${encodeURIComponent(courseId)}/classification-builder/execute-tests/`,
            { param_builder: builder }
          );
        } catch (err) {
          console.error(`Failed to execute tests for classification question ${question.id}`, err);
          failures.push(question.name || `#${question.id}`);
          failureMessages.push(
            `${question.name || `#${question.id}`}: ${formatApiError(
              err,
              "Cannot execute tests"
            )}`
          );
        } finally {
          setRunAllProgress({ completed: index + 1, total });
        }
      }

      await fetchQuestions();
      notifyUpdated();

      const parts = [
        `Executed CQ tests for ${total - failures.length} of ${total} runnable questions.`,
      ];
      if (skippedCount > 0) {
        parts.push(`Skipped ${skippedCount} without both tests and prompts.`);
      }
      if (failures.length > 0) {
        parts.push(`Failed: ${failures.join(", ")}.`);
        if (failureMessages.length > 0) parts.push(`First error: ${failureMessages[0]}.`);
      }
      showError(parts.join(" "), failures.length ? "warning" : "success");
    } finally {
      setRunningAllTests(false);
      setRunAllProgress({ completed: 0, total: 0 });
    }
  }, [
    courseId,
    fetchQuestions,
    questions,
    runAllDisabledReason,
    runnableQuestions,
    showError,
  ]);

  /**
   * Imports one question from a JSON export into this course. The server rejects a duplicate
   * name rather than replacing a question, and the POST is never repeated automatically: a
   * timeout may still have imported it. A response arriving after the author switched course
   * refreshes nothing, so it cannot land in the other course's list.
   */
  const importQuestion = useCallback(async (event) => {
    const input = event.target;
    const file = input.files?.[0];
    input.value = "";
    if (!file || !courseId) return;
    const validation = validateFile(file, QUESTION_IMPORT_FILE_RULES);
    if (!validation.valid) {
      showError(validation.error);
      return;
    }
    const importCourseId = courseId;
    setImporting(true);
    try {
      await longAction.run({
        label: "Importing question…",
        task: async () => {
          const formData = new FormData();
          formData.append("file", file);
          const { data } = await apiClient.post(
            `/backoffice/v1/courses/${encodeURIComponent(importCourseId)}/classification-questions/import/`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          return data;
        },
        success: (question) => `Imported "${question?.name || "the question"}".`,
        failure: formatQuestionImportError,
      });
      if (currentCourseIdRef.current !== importCourseId) return;
      await fetchQuestions();
      notifyUpdated();
    } catch (err) {
      console.error("Classification question import failed", err);
      if (!longAction.enabled) showError(formatQuestionImportError(err));
    } finally {
      setImporting(false);
    }
  }, [courseId, fetchQuestions, longAction, showError]);

  const openQuestion = useCallback(
    (questionId) => {
      if (!courseId || questionId == null) return;
      navigate(`/composer/courses/${courseId}/llm-questions/${questionId}`);
    },
    [courseId, navigate]
  );

  const runAllLabel = runningAllTests
    ? `Running (${runAllProgress.completed}/${runAllProgress.total})`
    : "Run all tests";
  const runAllTooltip = runAllDisabledReason
    ? `Can't run all tests: ${runAllDisabledReason}`
    : "Run every runnable question's tests";

  const titleRow = (
    <div className="pd-title-row llm-questions-title-row">
      <div className="pd-title-left">
        <h1 className="pd-title">LLM questions</h1>
      </div>
    </div>
  );

  const toolbarRunAllButton = (
    <Tooltip title={runAllTooltip} placement="top" arrow disableInteractive>
      <span className="llm-questions-run-all-wrap">
        <button
          type="button"
          className="pd-action-btn llm-questions-run-all"
          disabled={!!runAllDisabledReason}
          onClick={runAllQuestionTests}
        >
          {runningAllTests ? (
            <CircularProgress size={16} thickness={5} />
          ) : (
            <img src={"/images/run-all-tests.svg"} alt="Run all tests" />
          )}
          <span>{runAllLabel}</span>
        </button>
      </span>
    </Tooltip>
  );

  if (questions === null) {
    return (
      <>
        {titleRow}
        <div className="llm-questions-empty">Loading questions…</div>
      </>
    );
  }

  return (
    <>
      {titleRow}

      <div className="llm-questions-toolbar">
        <div className="llm-questions-search">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a question"
            aria-label="Search a question"
          />
          <SearchIcon className="llm-questions-search-icon" aria-hidden="true" />
        </div>
        <div className="llm-questions-toolbar-actions">
          <button
            type="button"
            className="pd-action-btn llm-questions-import"
            disabled={!courseId || importing}
            onClick={() => importInputRef.current?.click()}
          >
            <ImportIcon />
            <span>Import question</span>
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            hidden
            aria-label="Classification question JSON file"
            onChange={importQuestion}
          />
          {toolbarRunAllButton}
        </div>
      </div>

      {displayedQuestions.length === 0 ? (
        <div className="llm-questions-empty">
          {questions.length === 0
            ? "No classification questions yet. Use the sidebar \"+\" next to All LLM Questions to add one."
            : "No questions match your search."}
        </div>
      ) : (
        <div className="llm-questions-table llm-questions-table--flat" role="table">
          <div className="llm-questions-table-head" role="row">
            <span>Name</span>
            <span className="align-right">Chosen LLM</span>
            <span className="align-right">Correct</span>
            <span className="align-right">Incorrect</span>
            <span className="align-right">Exceptions</span>
            <span className="align-right">Actions</span>
          </div>
          <div className="llm-questions-list">
            {displayedQuestions.map((question) => {
              const correct = question?.chosen_prompt_stats?.percent_correct;
              const incorrect = question?.chosen_prompt_stats?.percent_incorrect;
              const exceptions = question?.chosen_prompt_stats?.percent_exceptions;
              return (
                <div
                  key={question.id}
                  className="llm-question-row"
                  role="row"
                  tabIndex={0}
                  onClick={() => openQuestion(question.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openQuestion(question.id);
                    }
                  }}
                >
                  <span className="llm-question-name" title={question?.name || ""}>
                    {question?.name || <em style={{ fontStyle: "italic" }}>Untitled</em>}
                  </span>
                  <span className="llm-question-cell align-right">
                    {getChosenPromptLlmLabel(question)}
                  </span>
                  <span className="llm-question-cell llm-question-cell--correct align-right">
                    {formatStat(correct)}
                  </span>
                  <span className="llm-question-cell llm-question-cell--incorrect align-right">
                    {formatStat(incorrect)}
                  </span>
                  <span className="llm-question-cell llm-question-cell--exceptions align-right">
                    {formatStat(exceptions)}
                  </span>
                  <span className="llm-question-actions align-right">
                    <button
                      type="button"
                      className="pd-action-btn llm-question-action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openQuestion(question.id);
                      }}
                    >
                      <span>Open</span>
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {ErrorPopupComponent}
    </>
  );
};

export default LlmQuestionsList;
